import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PaperclipIcon, PencilIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm, type DefaultValues, type FieldValues, type Path } from 'react-hook-form'
import { toast } from 'sonner'
import type { ZodType } from 'zod'

import { LoadingRows } from '@/components/skeletons'
import { ApiError } from '@/api/client'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { AttachmentPanel } from '@/features/attachments/AttachmentPanel'
import type { OwnerType } from '@/features/attachments/api'
import { cn } from '@/lib/utils'

/** One editable field in the entity form. */
export interface FieldDef<Values extends FieldValues> {
  name: Path<Values>
  label: string
  type?: 'text' | 'textarea' | 'url' | 'email' | 'number' | 'checkbox' | 'select' | 'eda'
  placeholder?: string
  description?: string
  options?: { value: string; label: string }[]
  /** Hide the field when editing (e.g. an immutable key). */
  createOnly?: boolean
  /** Lay the field out across the full dialog width. */
  wide?: boolean
}

/** One column in the entity table. */
export interface ColumnDef<Row> {
  header: string
  cell: (row: Row) => React.ReactNode
  className?: string
}

export interface EntityCrudProps<Row, Values extends FieldValues> {
  /** Plural, lower case: "manufacturers". */
  entity: string
  /** Singular, lower case: "manufacturer". */
  singular: string
  rows: Row[] | undefined
  isPending: boolean
  error: unknown
  columns: ColumnDef<Row>[]
  fields: FieldDef<Values>[]
  /** Input and output types match: the form edits exactly what it submits. */
  schema: ZodType<Values, Values>
  emptyValues: DefaultValues<Values>
  toValues: (row: Row) => DefaultValues<Values>
  labelOf: (row: Row) => string
  keyOf: (row: Row) => string
  onCreate: (values: Values) => Promise<unknown>
  onUpdate: (row: Row, values: Values) => Promise<unknown>
  onDelete: (row: Row) => Promise<unknown>
  /** Query key to invalidate after a write. */
  queryKey: readonly unknown[]
  /** Show the search box and filter client-side on the label. */
  searchable?: boolean
  /** Rows that must not be deleted, with the reason. */
  protectedRow?: (row: Row) => string | null
  /** Offer an attachments dialog for each row, owned by this type. */
  attachmentOwnerType?: OwnerType
}

export function EntityCrud<Row, Values extends FieldValues>(props: EntityCrudProps<Row, Values>) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<{ row: Row | null } | null>(null)
  const [deleting, setDeleting] = useState<Row | null>(null)
  const [attaching, setAttaching] = useState<Row | null>(null)
  const [blockers, setBlockers] = useState<ApiError | null>(null)

  const rows = useMemo(() => {
    const all = props.rows ?? []
    if (!props.searchable || !search.trim()) return all
    const needle = search.trim().toLowerCase()
    return all.filter((row) => props.labelOf(row).toLowerCase().includes(needle))
  }, [props, search])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: props.queryKey })

  const remove = useMutation({
    mutationFn: (row: Row) => props.onDelete(row),
    onSuccess: async () => {
      setDeleting(null)
      toast.success(`${capitalise(props.singular)} deleted`)
      await invalidate()
    },
    onError: (error) => {
      setDeleting(null)
      // A refused delete is not a failure to report as a toast — it is a list of
      // things the user has to deal with first.
      if (error instanceof ApiError && error.status === 409) setBlockers(error)
      else toast.error(error instanceof Error ? error.message : 'Could not delete')
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {props.searchable && (
          <div className="relative max-w-xs flex-1">
            <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`Search ${props.entity}…`}
              aria-label={`Search ${props.entity}`}
              className="pl-8"
            />
          </div>
        )}
        <Button className="ml-auto" onClick={() => setEditing({ row: null })}>
          <PlusIcon />
          New {props.singular}
        </Button>
      </div>

      {props.isPending ? (
        <LoadingRows label={`Loading ${props.entity}`} />
      ) : props.error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load {props.entity}</AlertTitle>
          <AlertDescription>
            {props.error instanceof Error ? props.error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="text-muted-foreground rounded-lg border border-dashed p-10 text-center text-sm">
          {search.trim() ? `No ${props.entity} match “${search}”.` : `No ${props.entity} yet.`}
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table stack>
            <TableHeader>
              <TableRow>
                {props.columns.map((column) => (
                  <TableHead key={column.header} className={column.className}>
                    {column.header}
                  </TableHead>
                ))}
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const locked = props.protectedRow?.(row) ?? null
                return (
                  <TableRow key={props.keyOf(row)}>
                    {props.columns.map((column, index) => (
                      <TableCell
                        key={column.header}
                        className={column.className}
                        data-grow={index === 0 || undefined}
                        data-label={index === 0 ? undefined : column.header}
                      >
                        {column.cell(row)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right whitespace-nowrap">
                      {props.attachmentOwnerType && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Attachments of ${props.labelOf(row)}`}
                          onClick={() => setAttaching(row)}
                        >
                          <PaperclipIcon />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Edit ${props.labelOf(row)}`}
                        onClick={() => setEditing({ row })}
                      >
                        <PencilIcon />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Delete ${props.labelOf(row)}`}
                        disabled={locked !== null}
                        title={locked ?? undefined}
                        onClick={() => setDeleting(row)}
                      >
                        <Trash2Icon />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {editing && (
        <EntityFormDialog
          {...props}
          row={editing.row}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await invalidate()
          }}
        />
      )}

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete “{deleting ? props.labelOf(deleting) : ''}”?</DialogTitle>
            <DialogDescription>This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              {remove.isPending && <Spinner />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {props.attachmentOwnerType && (
        <Dialog open={attaching !== null} onOpenChange={(open) => !open && setAttaching(null)}>
          <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Attachments of {attaching ? props.labelOf(attaching) : ''}</DialogTitle>
              <DialogDescription>
                Logos, catalogues, terms — anything worth keeping.
              </DialogDescription>
            </DialogHeader>
            {attaching && (
              <AttachmentPanel
                key={props.keyOf(attaching)}
                ownerType={props.attachmentOwnerType}
                ownerId={props.keyOf(attaching)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <BlockerDialog error={blockers} onClose={() => setBlockers(null)} />
    </div>
  )
}

/** Explains a refused delete: what is in the way, and a few examples. */
export function BlockerDialog({ error, onClose }: { error: ApiError | null; onClose: () => void }) {
  const blockers = error?.problem.blockers ?? []
  return (
    <Dialog open={error !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Still in use</DialogTitle>
          <DialogDescription>{error?.problem.detail}</DialogDescription>
        </DialogHeader>
        {blockers.length > 0 && (
          <ul className="flex flex-col gap-2 text-sm">
            {blockers.map((blocker) => (
              <li key={blocker.entity} className="rounded-md border p-3">
                <p className="font-medium">
                  {blocker.count} {blocker.entity}
                  {blocker.count === 1 ? '' : 's'}
                </p>
                {blocker.examples && blocker.examples.length > 0 && (
                  <p className="text-muted-foreground mt-1">
                    {blocker.examples.join(', ')}
                    {blocker.count > blocker.examples.length && ', …'}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EntityFormDialog<Row, Values extends FieldValues>({
  row,
  fields,
  schema,
  emptyValues,
  toValues,
  singular,
  onCreate,
  onUpdate,
  onClose,
  onSaved,
}: EntityCrudProps<Row, Values> & {
  row: Row | null
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: row ? toValues(row) : emptyValues,
  })

  const save = useMutation({
    mutationFn: (values: Values) => (row ? onUpdate(row, values) : onCreate(values)),
    onSuccess: async () => {
      toast.success(row ? `${capitalise(singular)} saved` : `${capitalise(singular)} created`)
      await onSaved()
    },
    onError: (error) => {
      // Field-level problems belong next to the field, not in a toast.
      if (error instanceof ApiError && error.problem.errors?.length) {
        for (const problem of error.problem.errors) {
          form.setError(problem.field as Path<Values>, { message: problem.message })
        }
        return
      }
      toast.error(error instanceof Error ? error.message : 'Could not save')
    },
  })

  const visible = fields.filter((field) => !(row && field.createOnly))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{row ? `Edit ${singular}` : `New ${singular}`}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit((values) => save.mutate(values as Values))}
          className="grid gap-4 sm:grid-cols-2"
          noValidate
        >
          {visible.map((field) => {
            const error = form.formState.errors[field.name]?.message as string | undefined
            const id = `field-${String(field.name)}`
            return (
              <div
                key={String(field.name)}
                className={cn(
                  'flex flex-col gap-2',
                  (field.wide || field.type === 'textarea') && 'sm:col-span-2',
                )}
              >
                {field.type === 'checkbox' ? (
                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox
                      id={id}
                      checked={Boolean(form.watch(field.name))}
                      onCheckedChange={(checked) =>
                        form.setValue(field.name, (checked === true) as never, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <Label htmlFor={id}>{field.label}</Label>
                  </div>
                ) : (
                  <>
                    <Label htmlFor={id}>{field.label}</Label>
                    {field.type === 'textarea' ? (
                      <Textarea id={id} {...form.register(field.name)} rows={3} />
                    ) : field.type === 'select' ? (
                      <Select
                        value={String(form.watch(field.name) ?? '')}
                        onValueChange={(value) =>
                          form.setValue(field.name, value as never, { shouldDirty: true })
                        }
                      >
                        <SelectTrigger id={id} aria-label={field.label}>
                          <SelectValue placeholder={field.placeholder ?? 'Choose…'} />
                        </SelectTrigger>
                        <SelectContent>
                          {(field.options ?? []).map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={id}
                        type={field.type === 'number' ? 'text' : (field.type ?? 'text')}
                        inputMode={field.type === 'number' ? 'decimal' : undefined}
                        placeholder={field.placeholder}
                        aria-invalid={error ? true : undefined}
                        {...form.register(field.name)}
                      />
                    )}
                  </>
                )}
                {field.description && !error && (
                  <p className="text-muted-foreground text-xs">{field.description}</p>
                )}
                {error && <p className="text-destructive text-xs">{error}</p>}
              </div>
            )
          })}

          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Spinner />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function capitalise(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
